import { getCookie } from '../../utils/helpers';
import { handle401Error, handle403Error } from '../../utils/authErrorHandler';
import request from '../../utils/request';

// Base API URL - mirrors momentsApi.ts convention
const API_BASE_URL =
  process.env.REACT_APP_API_V2_URL ||
  (process.env.NODE_ENV === 'development'
    ? 'http://localhost:8080/product-roadmap/api/v2'
    : '/product-roadmap/api/v2');

// Auth headers helper (pattern aligned with momentsApi.ts)
const getAuthHeaders = () => {
  const token = getCookie('app_access_token');
  return {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {})
  } as Record<string, string>;
};

// Extract a human-readable error message from API error bodies
const parseApiErrorMessage = (raw: string | undefined | null): string => {
  if (!raw) return '';
  const text = String(raw).trim();
  try {
    const data = JSON.parse(text);
    if (typeof data === 'string') return data;
    const topLevelMessage =
      data.message || data.errorMessage || data.error || data.detail;
    if (Array.isArray(data.fieldErrors) && data.fieldErrors.length > 0) {
      return data.fieldErrors[0].message || topLevelMessage || text;
    }
    if (Array.isArray(data.errors) && data.errors.length > 0) {
      return data.errors[0].message || topLevelMessage || text;
    }
    const msg = topLevelMessage || text;
    // Capitalize first alphabetic character for polish
    const idx = msg.search(/[A-Za-z]/);
    if (idx === -1) return msg;
    return (
      msg.slice(0, idx) + msg.charAt(idx).toUpperCase() + msg.slice(idx + 1)
    );
  } catch {
    const idx = text.search(/[A-Za-z]/);
    if (idx === -1) return text;
    return (
      text.slice(0, idx) + text.charAt(idx).toUpperCase() + text.slice(idx + 1)
    );
  }
};

// Types aligned to Features.tsx buildCreateFeaturePayload
export type PhasePayload = {
  type: string;
  date: string;
  quarters: string[];
  months: string[];
  value: string;
};

export interface CreateFeaturePayload {
  releaseId: string;
  productId: string;
  name: string;
  nameLink: string;
  riskLevel: string;
  status?: string;
  mvp: boolean;
  genAi?: boolean;
  consumesCredit?: boolean;
  demo: { link: string; date: string };
  ppc: string;
  prd: { link: string; date: string };
  surfaces: string[];
  internalBeta: PhasePayload;
  privateBeta: PhasePayload;
  publicBeta: PhasePayload;
  ga: PhasePayload;
}

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

/** POST /features may return the created row under `feature` (v2 envelope). */
export const extractCreatedFeatureFromResponse = (
  data: unknown
): Record<string, unknown> | undefined => {
  if (!data || typeof data !== 'object') return undefined;
  const body = data as Record<string, unknown>;

  // `{ status, data: { feature, moment, notifications } }`
  if (body.data && typeof body.data === 'object') {
    const inner = body.data as Record<string, unknown>;
    if (inner.feature && typeof inner.feature === 'object') {
      return inner.feature as Record<string, unknown>;
    }
  }

  const feature = body.feature;
  if (feature && typeof feature === 'object') {
    return feature as Record<string, unknown>;
  }
  return undefined;
};

export const extractCreatedFeatureId = (data: unknown): string | undefined => {
  const feature = extractCreatedFeatureFromResponse(data);
  if (feature) {
    const id = feature.featureId ?? feature.id ?? feature._id;
    if (id != null && String(id).trim() !== '') return String(id);
  }
  if (!data || typeof data !== 'object') return undefined;
  const body = data as Record<string, unknown>;
  const topLevel = body.featureId ?? body.id ?? body._id;
  if (topLevel != null && String(topLevel).trim() !== '')
    return String(topLevel);
  return undefined;
};

// Create Feature
export const createFeature = async <T = any>(
  payload: CreateFeaturePayload
): Promise<ApiResponse<T>> => {
  try {
    const url = `${API_BASE_URL}/features`;
    const response = await fetch(url, {
      method: 'POST',
      headers: getAuthHeaders(),
      credentials: 'include',
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      const errorText = await response.text();
      const parsedMessage = parseApiErrorMessage(errorText);
      // Map common error statuses as used elsewhere
      switch (response.status) {
        case 400:
          return {
            success: false,
            error: 'BAD_REQUEST',
            message: parsedMessage || 'Invalid feature payload'
          };
        case 401:
          handle401Error('Creating feature');
          return {
            success: false,
            error: 'UNAUTHORIZED',
            message: 'Authentication required'
          };
        case 403:
          handle403Error('Creating feature');
          return {
            success: false,
            error: 'ACCESS_DENIED',
            message: 'You do not have permission to create features'
          };
        case 404:
          return {
            success: false,
            error: 'ENDPOINT_NOT_FOUND',
            message: 'Features endpoint not found'
          };
        case 409:
          return {
            success: false,
            error: 'CONFLICT',
            message:
              parsedMessage || 'Feature already exists or conflicting data'
          };
        case 500:
          return {
            success: false,
            error: 'SERVER_ERROR',
            message:
              parsedMessage || 'Internal server error. Please try again later.'
          };
        default:
          return {
            success: false,
            error: 'API_ERROR',
            message:
              parsedMessage || `Request failed with status ${response.status}`
          };
      }
    }

    // If API returns the created feature, pass it through; otherwise just success
    let data: any = null;
    try {
      const responseText = await response.text();

      // Try to parse as JSON first
      if (responseText && responseText.trim().startsWith('{')) {
        data = JSON.parse(responseText);
      } else if (responseText) {
        // Handle text response like "Feature created successfully, with featureId 68ef80c67b"
        console.log('📝 API returned text response:', responseText);

        // Extract featureId from text response using regex
        const featureIdMatch = responseText.match(
          /featureId\s+([a-zA-Z0-9_-]+)/i
        );
        if (featureIdMatch && featureIdMatch[1]) {
          data = {
            featureId: featureIdMatch[1],
            message: responseText
          };
          console.log(
            '✅ Extracted featureId from text response:',
            data.featureId
          );
        } else {
          // No featureId found in text, just store the message
          data = {
            message: responseText
          };
        }
      }
    } catch (parseError) {
      console.warn('⚠️ Could not parse response:', parseError);
      // Some endpoints may return empty body on success
      data = null;
    }

    return { success: true, data };
  } catch (error) {
    return {
      success: false,
      error: 'NETWORK_ERROR',
      message:
        error instanceof Error
          ? error.message
          : 'Network error while creating feature'
    };
  }
};

// Update Feature (PATCH)
export const updateFeature = async <T = any>(
  featureId: string,
  updates: Partial<CreateFeaturePayload>
): Promise<ApiResponse<T>> => {
  try {
    const url = `${API_BASE_URL}/features/${encodeURIComponent(featureId)}`;
    const response = await fetch(url, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      credentials: 'include',
      body: JSON.stringify(updates)
    });

    if (!response.ok) {
      const errorText = await response.text();
      const parsedMessage = parseApiErrorMessage(errorText);
      switch (response.status) {
        case 400:
          return {
            success: false,
            error: 'BAD_REQUEST',
            message: parsedMessage
          };
        case 401:
          handle401Error('Updating feature');
          return {
            success: false,
            error: 'UNAUTHORIZED',
            message: 'Authentication required'
          };
        case 403:
          handle403Error('Updating feature');
          return {
            success: false,
            error: 'ACCESS_DENIED',
            message: 'Permission denied'
          };
        case 404:
          return {
            success: false,
            error: 'NOT_FOUND',
            message: parsedMessage || 'Feature not found'
          };
        case 409:
          return {
            success: false,
            error: 'CONFLICT',
            message: parsedMessage || 'Conflict updating feature'
          };
        case 500:
          return {
            success: false,
            error: 'SERVER_ERROR',
            message: parsedMessage || 'Internal server error'
          };
        default:
          return {
            success: false,
            error: 'API_ERROR',
            message: parsedMessage || `Status ${response.status}`
          };
      }
    }

    let data: any = null;
    try {
      data = await response.json();
    } catch {}
    return { success: true, data };
  } catch (error) {
    return {
      success: false,
      error: 'NETWORK_ERROR',
      message:
        error instanceof Error
          ? error.message
          : 'Network error while updating feature'
    };
  }
};

export interface ReorderReleaseFeaturesBody {
  features: { featureId: string; displayOrder: number }[];
}

/** PATCH reorder for all features under a release (issue). Body omits issueId — it is in the path. */
export const reorderReleaseFeatures = async (
  issueId: string,
  body: ReorderReleaseFeaturesBody
): Promise<ApiResponse<unknown>> => {
  const url = `${API_BASE_URL}/features/reorder/${encodeURIComponent(issueId)}`;
  try {
    // Parsed JSON from the server (204 → `null`; 200 may return a body). Same value `request` uses internally.
    const result = await request(url, {
      method: 'PATCH',
      body: JSON.stringify(body),
      credentials: 'include' as RequestCredentials
    });

    // HTTP 400 may not throw in `request`; error JSON sometimes includes `status` >= 400.
    const payload = result as { status?: number } | null | undefined;
    if (typeof payload?.status === 'number' && payload.status >= 400) {
      throw new Error();
    }
    return { success: true, data: result };
  } catch (e) {
    return {
      success: false,
      error: 'REQUEST_FAILED',
      message: 'Unable to save feature order.'
    };
  }
};

// Delete Feature
export const deleteFeature = async (
  featureId: string
): Promise<ApiResponse<unknown>> => {
  try {
    const url = `${API_BASE_URL}/features/${encodeURIComponent(featureId)}`;
    const response = await fetch(url, {
      method: 'DELETE',
      headers: getAuthHeaders(),
      credentials: 'include'
    });

    if (!response.ok) {
      const errorText = await response.text();
      const parsedMessage = parseApiErrorMessage(errorText);
      switch (response.status) {
        case 401:
          handle401Error('Deleting feature');
          return {
            success: false,
            error: 'UNAUTHORIZED',
            message: 'Authentication required'
          };
        case 403:
          handle403Error('Deleting feature');
          return {
            success: false,
            error: 'ACCESS_DENIED',
            message: 'Permission denied'
          };
        case 404:
          return {
            success: false,
            error: 'NOT_FOUND',
            message: parsedMessage || 'Feature not found'
          };
        case 500:
          return {
            success: false,
            error: 'SERVER_ERROR',
            message: parsedMessage || 'Internal server error'
          };
        default:
          return {
            success: false,
            error: 'API_ERROR',
            message: parsedMessage || `Status ${response.status}`
          };
      }
    }

    let data: unknown = null;
    try {
      data = await response.json();
    } catch {
      // empty or non-JSON body on success is fine
    }
    return { success: true, data };
  } catch (error) {
    return {
      success: false,
      error: 'NETWORK_ERROR',
      message:
        error instanceof Error
          ? error.message
          : 'Network error while deleting feature'
    };
  }
};

// ========================= FETCH FEATURES BY ISSUE =========================

export interface FeaturesApiGroup {
  productName: string;
  feature: any[]; // Raw feature items
}

export interface FeaturesApiResponse {
  success: boolean;
  data?: FeaturesApiGroup[];
  error?: string;
  message?: string;
}

export const fetchFeaturesByIssue = async (
  issueId: string
): Promise<FeaturesApiResponse> => {
  try {
    const url = `${API_BASE_URL}/features/issueId/${encodeURIComponent(
      issueId
    )}`;
    const response = await fetch(url, {
      method: 'GET',
      headers: getAuthHeaders(),
      credentials: 'include'
    });

    if (!response.ok) {
      const errorText = await response.text();
      switch (response.status) {
        case 404:
          return {
            success: false,
            error: 'FEATURES_NOT_FOUND',
            message: 'Features not found for this issueId'
          };
        case 401:
          handle401Error('Fetching features');
          return {
            success: false,
            error: 'UNAUTHORIZED',
            message: 'Authentication required'
          };
        case 403:
          handle403Error('Fetching features');
          return {
            success: false,
            error: 'ACCESS_DENIED',
            message: 'You do not have permission to access features'
          };
        case 500:
          return {
            success: false,
            error: 'SERVER_ERROR',
            message: 'Internal server error. Please try again later.'
          };
        default:
          return {
            success: false,
            error: 'API_ERROR',
            message: `Features API request failed with status ${response.status}`
          };
      }
    }

    const data = await response.json();

    // Flexible mapping: support grouped or flat arrays
    let groups: FeaturesApiGroup[] = [];
    if (Array.isArray(data)) {
      if (
        data.length > 0 &&
        (data[0] as any)?.feature &&
        Array.isArray((data[0] as any).feature)
      ) {
        groups = data as FeaturesApiGroup[];
      } else {
        // Handle array of group objects like [{"productName":"X","features":[...]}]
        groups = (data as any[]).map(item => ({
          productName:
            (item as any).productName || (item as any).product || 'Product',
          feature: Array.isArray((item as any).features)
            ? (item as any).features
            : Array.isArray((item as any).feature)
            ? (item as any).feature
            : []
        }));
      }
    } else if (Array.isArray((data as any)?.features)) {
      // Normalize groups that use `features` key to our `feature` key
      const rawGroups = (data as any).features as any[];
      groups = rawGroups.map(g => ({
        productName: g.productName || g.product || 'Product',
        feature: Array.isArray(g.features) ? g.features : []
      }));
    } else if (Array.isArray((data as any)?.items)) {
      const items = (data as any).items as any[];
      const byProduct: Record<string, any[]> = {};
      items.forEach(item => {
        const productName = item.productName || item.product || 'Product';
        byProduct[productName] = byProduct[productName] || [];
        byProduct[productName].push(item);
      });
      groups = Object.keys(byProduct).map(productName => ({
        productName,
        feature: byProduct[productName]
      }));
    }

    return { success: true, data: groups };
  } catch (error) {
    return {
      success: false,
      error: 'NETWORK_ERROR',
      message:
        error instanceof Error
          ? error.message
          : 'Network error while fetching features'
    };
  }
};
